import { useState, useEffect, useRef, useCallback } from 'react';
import { formatDate, stripHtml } from '../utils';
import ExportMenu from './ExportMenu';

// ─── SPINE TIMELINE COMPONENT ─────────────────────────────────────────────────
//
//  Renders events as a long-form book / editorial reading experience.
//  Each event is a chapter with pull quote, drop cap, hero image, and footnotes.
//
//  Props:
//    events       {array}    event objects
//    colors       {object}   { primary, secondary, tertiary }
//    onEventClick {function} opens event detail modal
//    manualOrder  {boolean}
//    timeline     {object}   full timeline (for title, description, id)
//    canEdit      {boolean}  shows photo-prompt in editor
//    onEditEvent  {function} opens event form (editor only)
//

// ── Utilities ─────────────────────────────────────────────────────────────────

const WPM = 200;

const wc = (html) => {
  if (!html) return 0;
  const t = html.replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim();
  return t ? t.split(' ').length : 0;
};

const pullQuoteFrom = (html, override) => {
  if (override?.trim()) return override.trim();
  const text = html.replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim();
  const m = text.match(/^(.{40,200}[.!?])\s/);
  return m ? m[1] : text.slice(0, 160);
};

// Wraps highlight terms in <mark> without breaking HTML tags
const highlightHtml = (html, terms) => {
  if (!terms?.length || !html) return html;
  let out = html;
  [...terms].sort((a, b) => b.length - a.length).forEach(term => {
    if (!term.trim()) return;
    const esc = term.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    out = out.replace(new RegExp(`(<[^>]+>)|(${esc})`, 'gi'),
      (m, tag, txt) => tag || `<mark class="sp-hl">${txt}</mark>`);
  });
  return out;
};

const ORNAMENT = '· · ·';

// ─────────────────────────────────────────────────────────────────────────────

const SpineTimeline = ({ events, colors, onEventClick, manualOrder, timeline, canEdit, onEditEvent, isEmbed = false }) => {
  const sorted = [...events].sort((a, b) => {
    if (manualOrder) return (a.sortOrder ?? 0) - (b.sortOrder ?? 0);
    return (a.startDate || "").localeCompare(b.startDate || "");
  });

  // ── State ──────────────────────────────────────────────────────────────────
  const [tocOpen,       setTocOpen]       = useState(true);
  const [activeIdx,     setActiveIdx]     = useState(0);
  const [scrollPct,     setScrollPct]     = useState(0);
  const [focusMode,     setFocusMode]     = useState(false);
  const [showScrollTop, setShowScrollTop] = useState(false);
  const [selTip,        setSelTip]        = useState(null); // { x, y, text, evId }
  const [fontSize,      setFontSize]      = useState(() => parseInt(localStorage.getItem('sp-font-size') || '18'));
  const [serifBody,     setSerifBody]     = useState(() => localStorage.getItem('sp-serif-body') !== 'false');
  const [highlights,    setHighlights]    = useState(() => {
    try { return JSON.parse(localStorage.getItem(`sp-hl-${timeline?.id}`) || '{}'); } catch { return {}; }
  });

  const rootRef    = useRef(null);
  const chapterEls = useRef([]);

  // ── Computed ───────────────────────────────────────────────────────────────
  const totalWords   = sorted.reduce((s, ev) => s + wc(ev.description || ''), 0);
  const totalMins    = Math.max(1, Math.ceil(totalWords / WPM));
  const remainWords  = sorted.slice(activeIdx).reduce((s, ev) => s + wc(ev.description || ''), 0);
  const remainMins   = Math.max(0, Math.ceil(remainWords / WPM));
  const hasHighlights = Object.values(highlights).some(a => a?.length);

  // ── Scroll tracking (tracks on the root scroll container) ─────────────────
  useEffect(() => {
    const root = rootRef.current;
    if (!root) return;
    const onScroll = () => {
      const { scrollTop, scrollHeight, clientHeight } = root;
      const max = scrollHeight - clientHeight;
      setScrollPct(max > 0 ? (scrollTop / max) * 100 : 0);
      setShowScrollTop(scrollTop > 400);
      let next = 0;
      for (let i = sorted.length - 1; i >= 0; i--) {
        const el = chapterEls.current[i];
        if (!el) continue;
        if (el.offsetTop - root.scrollTop <= 160) { next = i; break; }
      }
      setActiveIdx(next);
    };
    root.addEventListener('scroll', onScroll, { passive: true });
    return () => root.removeEventListener('scroll', onScroll);
  }, [sorted.length]);

  // ── Text selection → highlight tooltip ────────────────────────────────────
  useEffect(() => {
    const onUp = () => {
      const sel = window.getSelection();
      if (!sel || sel.isCollapsed || sel.toString().trim().length < 3) { setSelTip(null); return; }
      const text = sel.toString().trim();
      let node = sel.anchorNode?.parentElement;
      let evId = null;
      while (node) { if (node.dataset?.evid) { evId = node.dataset.evid; break; } node = node.parentElement; }
      if (!evId) { setSelTip(null); return; }
      const rect = sel.getRangeAt(0).getBoundingClientRect();
      setSelTip({ x: rect.left + rect.width / 2, y: rect.top + window.scrollY - 44, text, evId });
    };
    document.addEventListener('mouseup', onUp);
    return () => document.removeEventListener('mouseup', onUp);
  }, []);

  // ── Handlers ───────────────────────────────────────────────────────────────
  const scrollToChapter = useCallback((i) => {
    const el = chapterEls.current[i];
    const root = rootRef.current;
    if (!el || !root) return;
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    root.scrollTo({ top: el.offsetTop - 72, behavior: reduced ? 'auto' : 'smooth' });
  }, []);

  const scrollToTop = useCallback(() => {
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    rootRef.current?.scrollTo({ top: 0, behavior: reduced ? 'auto' : 'smooth' });
  }, []);

  const handleBeforeCapture = useCallback(async () => {
    if (rootRef.current) {
      rootRef.current.style.height = 'auto';
      rootRef.current.style.overflow = 'visible';
      await new Promise(r => requestAnimationFrame(() => requestAnimationFrame(r)));
    }
  }, []);

  const handleAfterCapture = useCallback(() => {
    if (rootRef.current) {
      rootRef.current.style.height = '';
      rootRef.current.style.overflow = '';
    }
  }, []);

  const addHighlight = useCallback((evId, text) => {
    setHighlights(prev => {
      const updated = { ...prev, [evId]: [...(prev[evId] || []), text] };
      try { localStorage.setItem(`sp-hl-${timeline?.id}`, JSON.stringify(updated)); } catch {}
      return updated;
    });
    setSelTip(null);
    window.getSelection()?.removeAllRanges();
  }, [timeline?.id]);

  const clearHighlights = useCallback(() => {
    setHighlights({});
    try { localStorage.removeItem(`sp-hl-${timeline?.id}`); } catch {}
  }, [timeline?.id]);

  const setFs = (n) => { setFontSize(n); try { localStorage.setItem('sp-font-size', n); } catch {} };
  const toggleSerif = () => setSerifBody(v => { try { localStorage.setItem('sp-serif-body', !v); } catch {} return !v; });

  // ── Empty guard (after all hooks) ──────────────────────────────────────────
  if (!sorted.length) return (
    <div style={{ padding: 60, textAlign: 'center', color: 'var(--text3)' }}>
      No events yet. Add your first event.
    </div>
  );

  const bodyFont = serifBody
    ? "'Playfair Display', Georgia, serif"
    : "'DM Sans', var(--font-body), sans-serif";

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <div
      ref={rootRef}
      className={`sp-root${focusMode ? ' sp-focus-mode' : ''}`}
      style={{ '--sp-p': colors.primary, '--sp-s': colors.secondary, '--sp-t': colors.tertiary, '--sp-fs': `${fontSize}px`, '--sp-bf': bodyFont }}
    >
      {/* ── Reading progress bar ── */}
      <div className="sp-prog-bar">
        <div className="sp-prog-fill" style={{ width: `${scrollPct}%`, background: colors.primary }} />
      </div>

      {/* ── Main grid: reading column + sticky chapter nav ── */}
      <div className="sp-grid">

        {/* ── Reading column ── */}
        <div className="sp-col">

          {/* Book header */}
          <header className="sp-book-hdr" style={{ borderBottomColor: colors.primary + '33' }}>
            <div className="sp-book-meta-row">
              <span className="sp-book-badge" style={{ color: colors.primary, borderColor: colors.primary + '55' }}>
                {sorted.length} {sorted.length === 1 ? 'Chapter' : 'Chapters'}
              </span>
              <span className="sp-book-rt" style={{ color: 'var(--text3)' }}>~{totalMins} min read</span>
            </div>
            <h1 className="sp-book-title">{timeline?.title || 'Timeline'}</h1>
            {stripHtml(timeline?.description || '') && (
              <p className="sp-book-desc">{stripHtml(timeline.description).slice(0, 280)}</p>
            )}
          </header>

          {/* Table of contents */}
          <div className="sp-toc" style={{ borderColor: colors.primary + '25' }}>
            <button className="sp-toc-toggle" onClick={() => setTocOpen(o => !o)} style={{ color: colors.primary }}>
              <span>{tocOpen ? '▾' : '▸'}</span> Table of Contents
            </button>
            {tocOpen && (
              <ol className="sp-toc-list">
                {sorted.map((ev, i) => (
                  <li key={ev.id}>
                    <button className="sp-toc-link" onClick={() => scrollToChapter(i)}>
                      <span className="sp-toc-n" style={{ color: colors.primary }}>{String(i + 1).padStart(2, '0')}</span>
                      <span className="sp-toc-ev">{ev.title}</span>
                      <span className="sp-toc-dt" style={{ color: 'var(--text3)' }}>
                        {formatDate(ev.startDate, ev.startEra !== 'NONE')}
                      </span>
                    </button>
                  </li>
                ))}
              </ol>
            )}
          </div>

          {/* Controls */}
          <div className="sp-ctrl-bar">
            <div className="sp-fs-btns">
              {[16, 18, 20].map(n => (
                <button key={n} className={`sp-fs-btn${fontSize === n ? ' sp-ctrl-on' : ''}`}
                  onClick={() => setFs(n)}
                  style={fontSize === n ? { borderColor: colors.primary, color: colors.primary } : {}}>
                  {n === 16 ? 'A−' : n === 18 ? 'A' : 'A+'}
                </button>
              ))}
            </div>
            <button className={`sp-toggle-btn${!serifBody ? ' sp-ctrl-on' : ''}`} onClick={toggleSerif}
              style={!serifBody ? { borderColor: colors.primary, color: colors.primary } : {}}>
              {serifBody ? 'Serif' : 'Sans'}
            </button>
            <button className={`sp-toggle-btn${focusMode ? ' sp-ctrl-on' : ''}`} onClick={() => setFocusMode(v => !v)}
              style={focusMode ? { borderColor: colors.primary, color: colors.primary } : {}}>
              Focus
            </button>
            {hasHighlights && (
              <button className="sp-toggle-btn sp-clr-hl" onClick={clearHighlights} title="Clear all highlights">
                Clear Highlights
              </button>
            )}
            {!isEmbed && timeline && (
              <ExportMenu
                timeline={timeline}
                captureRef={rootRef}
                formats={['pdf', 'embed']}
                onBeforeCapture={handleBeforeCapture}
                onAfterCapture={handleAfterCapture}
              />
            )}
          </div>

          {/* Spine line + chapters */}
          <div className="sp-body-wrap">
            <div className="sp-spine-line" style={{ background: colors.primary }} />

            {sorted.map((ev, i) => {
              const hlTerms  = highlights[ev.id] || [];
              const bodyHtml = highlightHtml(ev.description || '', hlTerms);
              const pull     = pullQuoteFrom(ev.description || '', ev.pullQuote || '');
              const chNum    = String(i + 1).padStart(2, '0');
              const isActive = activeIdx === i;

              return (
                <section
                  key={ev.id}
                  ref={el => chapterEls.current[i] = el}
                  className={`sp-chapter${focusMode ? (isActive ? ' sp-ch-active' : ' sp-ch-dim') : ''}`}
                  data-evid={ev.id}
                >
                  {/* Spine marker dot */}
                  <div className="sp-spine-dot" style={{ background: colors.primary }} />

                  {/* Chapter label */}
                  <p className="sp-ch-label" style={{ color: colors.secondary }}>CHAPTER {chNum}</p>

                  {/* Watermark number */}
                  <div className="sp-ch-watermark" aria-hidden="true" style={{ color: colors.primary }}>{chNum}</div>

                  {/* Title */}
                  <h2 className="sp-ch-title">{ev.title}</h2>

                  {/* Byline */}
                  <p className="sp-ch-byline" style={{ color: colors.secondary }}>
                    {formatDate(ev.startDate, ev.startEra !== 'NONE')}
                    {ev.startDate !== ev.endDate && <> — {formatDate(ev.endDate, ev.endEra !== 'NONE')}</>}
                  </p>

                  {/* Hero image */}
                  {ev.photo && (
                    <figure className="sp-hero">
                      <img src={ev.photo} alt={ev.title} className="sp-hero-img" loading="lazy" />
                      <figcaption className="sp-hero-cap">{ev.title}</figcaption>
                    </figure>
                  )}

                  {/* Photo prompt — editor only */}
                  {canEdit && !ev.photo && onEditEvent && (
                    <button className="sp-photo-prompt" onClick={() => onEditEvent(ev)}>
                      📷 Add a photo to this chapter
                    </button>
                  )}

                  {/* Pull quote */}
                  {pull && (
                    <blockquote className="sp-pull" style={{ borderLeftColor: colors.secondary }}>
                      <span className="sp-pull-mark" aria-hidden="true" style={{ color: colors.primary }}>"</span>
                      {pull}
                    </blockquote>
                  )}

                  {/* Body text */}
                  {ev.description ? (
                    <div
                      className="sp-body"
                      data-evid={ev.id}
                      dangerouslySetInnerHTML={{ __html: bodyHtml }}
                    />
                  ) : (
                    <p className="sp-body-empty">No description for this chapter yet.</p>
                  )}

                  {/* Sources footnotes */}
                  {ev.sources?.length > 0 && (
                    <footer className="sp-footnotes" style={{ borderTopColor: colors.primary + '25' }}>
                      <p className="sp-fn-label">Sources</p>
                      <ol className="sp-fn-list">
                        {ev.sources.map((src, si) => (
                          <li key={src.id || si} className="sp-fn-item">
                            {src.label && <strong className="sp-fn-title">{src.label}</strong>}
                            {src.text  && <span className="sp-fn-text"> — {src.text.slice(0, 400)}{src.text.length > 400 ? '…' : ''}</span>}
                          </li>
                        ))}
                      </ol>
                    </footer>
                  )}

                  {/* Chapter actions */}
                  <div className="sp-ch-actions">
                    <button className="sp-view-btn" onClick={() => onEventClick(ev)}
                      style={{ borderColor: colors.primary + '55', color: colors.primary }}>
                      View Details
                    </button>
                    <button className="sp-print-btn" onClick={() => {
                      const w = window.open('', '_blank');
                      w.document.write(`<!doctype html><html><head><title>${ev.title}</title><style>body{font-family:Georgia,serif;max-width:680px;margin:40px auto;line-height:1.8;font-size:18px;color:#111}h1{font-size:32px;margin-bottom:4px}p.bl{color:#777;font-style:italic;margin-bottom:32px}blockquote{border-left:3px solid #ccc;margin:24px 0;padding:0 24px;font-size:22px;font-style:italic}img{max-width:100%;margin:24px 0}@media print{*{-webkit-print-color-adjust:exact}}</style></head><body><h1>${ev.title}</h1><p class="bl">${formatDate(ev.startDate, ev.startEra !== 'NONE')}${ev.startDate !== ev.endDate ? ` — ${formatDate(ev.endDate, ev.endEra !== 'NONE')}` : ''}</p>${ev.description || '<p>No description.</p>'}</body></html>`);
                      w.document.close();
                      setTimeout(() => w.print(), 400);
                    }}>
                      Print Chapter
                    </button>
                  </div>

                  {/* Chapter divider */}
                  {i < sorted.length - 1 && (
                    <div className="sp-divider" style={{ color: colors.primary + '70' }} aria-hidden="true">
                      {ORNAMENT}
                    </div>
                  )}
                </section>
              );
            })}
          </div>
        </div>

        {/* ── Chapter navigation (sticky right column) ── */}
        <aside className="sp-ch-nav">
          <div className="sp-nav-inner">
            <p className="sp-nav-curr" style={{ color: colors.primary }}>
              {sorted[activeIdx]?.title.slice(0, 28)}{(sorted[activeIdx]?.title.length || 0) > 28 ? '…' : ''}
            </p>
            <div className="sp-nav-dots" role="tablist">
              {sorted.map((ev, i) => (
                <button key={ev.id} role="tab" aria-selected={activeIdx === i}
                  className={`sp-nav-dot${activeIdx === i ? ' sp-nav-dot-on' : ''}`}
                  onClick={() => scrollToChapter(i)} title={ev.title}
                  style={activeIdx === i ? { background: colors.primary, borderColor: colors.primary } : { borderColor: colors.secondary + '50' }}
                />
              ))}
            </div>
            <p className="sp-nav-remain" style={{ color: 'var(--text3)' }}>
              {remainMins > 0 ? `${remainMins} min left` : 'Finished'}
            </p>
          </div>
        </aside>
      </div>

      {/* ── Scroll to top ── */}
      {showScrollTop && (
        <button className="sp-top-btn" onClick={scrollToTop} title="Back to top"
          style={{ background: colors.primary }}>
          ↑
        </button>
      )}

      {/* ── Highlight tooltip ── */}
      {selTip && (
        <div className="sp-sel-tip" style={{ left: selTip.x, top: selTip.y }}>
          <button className="sp-sel-tip-add" onClick={() => addHighlight(selTip.evId, selTip.text)}
            style={{ background: colors.primary }}>
            Highlight
          </button>
          <button className="sp-sel-tip-x" onClick={() => setSelTip(null)}>✕</button>
        </div>
      )}
    </div>
  );
};

export default SpineTimeline;
